import {
  Dialog,
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
} from "@mui/material";
import { InspModelItem } from "@types";
import { RuleItem } from "@types";
import { ReactNode, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { addInspRule, editInspRule } from "../../services/inspRuleService";
import {
  deleteInspModel,
  editInspModel,
  getInspModel,
} from "../../services/modelService";
import { parseStringToArray } from "../../utils/utils";
import { Edit } from "lucide-react";
import { t } from "i18next";
import { toast } from "react-toastify";
import { getProjectGeneralInfo } from "../../services/projectService";
import { macBtn } from "../config/DevModeModal";

const FONT =
  '-apple-system, BlinkMacSystemFont, "SF Pro Text", "Apple SD Gothic Neo", "Helvetica Neue", sans-serif';

const inputSx = {
  "& .MuiOutlinedInput-root": {
    fontSize: 13,
    borderRadius: "8px",
    backgroundColor: "var(--dk-surface, #fff)",
    "& fieldset": { borderColor: "var(--dk-border, rgba(0,0,0,0.12))" },
  },
};

const smallBtn = (kind: "primary" | "secondary" | "danger") => ({
  ...macBtn(kind),
  fontSize: 12.5,
  py: 0.5,
  px: 1.75,
});

// Mac 설정 패널 스타일 섹션 (제목 + 흰 박스)
const Section = ({
  title,
  action,
  children,
}: {
  title: ReactNode;
  action?: ReactNode;
  children: ReactNode;
}) => (
  <Box sx={{ mb: 2.5 }}>
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        px: 0.5,
        mb: 0.75,
      }}
    >
      <Typography
        sx={{
          fontFamily: FONT,
          fontSize: 12,
          fontWeight: 600,
          color: "var(--dk-text-2, #86868b)",
        }}
      >
        {title}
      </Typography>
      {action}
    </Box>
    <Box
      sx={{
        bgcolor: "var(--dk-surface, #fff)",
        borderRadius: "10px",
        border: "1px solid var(--dk-border, rgba(0,0,0,0.08))",
        px: 2,
        py: 1.5,
      }}
    >
      {children}
    </Box>
  </Box>
);

const Row = ({ label, children }: { label: string; children: ReactNode }) => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: "110px 1fr",
      alignItems: "center",
      py: 0.75,
      gap: 1.5,
    }}
  >
    <Typography sx={{ fontSize: 13, color: "var(--dk-text-2, #86868b)" }}>{label}</Typography>
    <Box sx={{ fontSize: 13, color: "var(--dk-text, #1d1d1f)", fontWeight: 500, minWidth: 0 }}>
      {children}
    </Box>
  </Box>
);

const Stat = ({
  label,
  value,
  color,
}: {
  label: string;
  value: ReactNode;
  color: string;
}) => (
  <Box sx={{ flex: 1, textAlign: "center", py: 0.5 }}>
    <Typography
      sx={{ fontSize: 17, fontWeight: 600, color, letterSpacing: "-0.02em" }}
    >
      {value ?? "-"}
    </Typography>
    <Typography sx={{ fontSize: 11, color: "var(--dk-text-2, #86868b)", mt: 0.25 }}>
      {label}
    </Typography>
  </Box>
);

interface Props {
  model: InspModelItem | null;
  ruleList: RuleItem[];
  setRuleList: (rule: RuleItem[]) => void;
  open: boolean;
  onClose: () => void;
  initialEdit?: boolean;
  onUpdated: (model: InspModelItem) => void;
  onDeleted: (id: number) => void;
}

const emptyRuleForm = {
  name: "",
  description: "",
  image_regex: "",
  recipe_regex: "",
};

export default function ModelDetailModal({
  model,
  ruleList,
  setRuleList,
  open,
  onClose,
  initialEdit = false,
  onUpdated,
  onDeleted,
}: Props) {
  const navigate = useNavigate();
  const [editingRuleId, setEditingRuleId] = useState<number | null>(null);
  const [ruleForm, setRuleForm] = useState(emptyRuleForm);
  const [isAddingRule, setIsAddingRule] = useState(false);
  const [isEditingModel, setIsEditingModel] = useState(initialEdit);
  const [modelForm, setModelForm] = useState({ name: "", description: "" });

  const [info, setInfo] = useState<any>(null); // info 구성은 모델마다 다르거나 null일 수 있음
  const [infoLoading, setInfoLoading] = useState(false);

  useEffect(() => {
    if (open && model) {
      setIsEditingModel(initialEdit);
      setModelForm({ name: model.name, description: model.description ?? "" });
    }
  }, [open, model, initialEdit]);

  // 모델 선택 시 상세 정보 조회 (options 제외)
  useEffect(() => {
    if (!open || !model) return;
    let cancelled = false;
    setInfo(null);
    setInfoLoading(true);
    getInspModel(model.id, { omit: "options" })
      .then((detail) => !cancelled && setInfo(detail?.info ?? null))
      .catch((error) => !cancelled && toast.error(error.message))
      .finally(() => !cancelled && setInfoLoading(false));
    return () => {
      cancelled = true;
    };
  }, [open, model?.id]);

  if (!model) return null;

  const projectId = info?.project_id;
  const performance = info?.performance;
  const fmt = (v: unknown) =>
    typeof v === "number" ? Number(v.toFixed(4)) : null;
  const rules = ruleList.filter((rule) => rule.inspModel.id === model.id);

  const handleMoveProject = async () => {
    if (!projectId) return;
    try {
      await getProjectGeneralInfo(projectId);
    } catch {
      toast.warn(t("LinkedProjectDeletedMsg"));
      return;
    }
    navigate(`/main/project/${projectId}`);
  };

  const handleSaveModel = async () => {
    const name = modelForm.name.trim();
    if (!name) {
      toast.warn("모델 이름을 입력해주세요.");
      return;
    }
    try {
      await editInspModel(model.id, {
        name,
        description: modelForm.description,
      });
      onUpdated({ ...model, name, description: modelForm.description });
      setIsEditingModel(false);
      toast.success("검사모델이 수정되었습니다.");
    } catch (error: any) {
      toast.error(error.message);
    }
  };

  const handleDeleteModel = async () => {
    if (!window.confirm(`'${model.name}' ${t("DeleteConfirmMsg")}`)) return;
    try {
      await deleteInspModel(model.id);
      onDeleted(model.id);
      toast.success("검사모델이 삭제되었습니다.");
    } catch (error: any) {
      toast.error(error.message);
    }
  };

  const handleEditRule = (rule: RuleItem) => {
    setEditingRuleId(rule.id);
    setRuleForm({
      name: rule.name || "",
      description: rule.description || "",
      image_regex: rule.image_regex ?? "",
      recipe_regex: rule.recipe_regex ?? "",
    });
  };

  const handleSaveRule = async (ruleId: number) => {
    await editInspRule(ruleId, { ...ruleForm });
    setRuleList(
      ruleList.map((r) => (r.id === ruleId ? { ...r, ...ruleForm } : r)),
    );
    setEditingRuleId(null);
  };

  const handleAddRule = async () => {
    const response = await addInspRule({
      description: ruleForm.description,
      name: ruleForm.name,
      model_uuid: model.uuid,
      recipe_regex: parseStringToArray(ruleForm.recipe_regex),
      image_regex: parseStringToArray(ruleForm.image_regex),
    });

    if (response) {
      const newRule: RuleItem = {
        id: response.id ?? Date.now(),
        ...ruleForm,
        inspModel: {
          id: model.id,
          uuid: model.uuid,
          name: model.name,
          info: model.info,
        },
      };
      setRuleList([...ruleList, newRule]);
    }
    setIsAddingRule(false);
  };

  const ruleFields = (withPlaceholder: boolean) => (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "100px 1fr",
        gap: 1,
        alignItems: "center",
      }}
    >
      {(
        [
          ["name", t("InspRuleName"), ""],
          ["description", t("Description"), ""],
          ["image_regex", t("ImageRegex"), t("ImageRegexExamplePlaceholder")],
          [
            "recipe_regex",
            t("RecipeRegex"),
            t("RecipeRegexExamplePlaceholder"),
          ],
        ] as const
      ).map(([key, label, placeholder]) => (
        <Box key={key} sx={{ display: "contents" }}>
          <Typography sx={{ fontSize: 12.5, color: "var(--dk-text-2, #86868b)" }}>
            {label}
          </Typography>
          <TextField
            size="small"
            placeholder={withPlaceholder ? placeholder : undefined}
            value={ruleForm[key]}
            onChange={(e) =>
              setRuleForm({ ...ruleForm, [key]: e.target.value })
            }
            sx={inputSx}
          />
        </Box>
      ))}
    </Box>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: "rgba(0,0,0,0.25)",
            backdropFilter: "blur(4px)",
          },
        },
      }}
      PaperProps={{
        sx: {
          width: 560,
          maxWidth: "calc(100vw - 32px)",
          maxHeight: "80vh",
          borderRadius: "14px",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          backgroundColor: "rgba(246,246,248,0.96)",
          backdropFilter: "blur(20px)",
          border: "1px solid var(--dk-border, rgba(0,0,0,0.08))",
          boxShadow: "0 20px 60px rgba(0,0,0,0.28)",
          fontFamily: FONT,
          "& .MuiTypography-root": { fontFamily: FONT },
        },
      }}
    >
      {/* 신호등 헤더 */}
      <Box
        sx={{
          position: "relative",
          display: "flex",
          alignItems: "center",
          gap: "8px",
          px: 2,
          py: 1.25,
          borderBottom: "1px solid var(--dk-border, rgba(0,0,0,0.06))",
          flexShrink: 0,
        }}
      >
        {["#ff5f57", "#febc2e", "#28c840"].map((c, i) => (
          <Box
            key={c}
            onClick={i === 0 ? onClose : undefined}
            sx={{
              width: 12,
              height: 12,
              borderRadius: "50%",
              backgroundColor: c,
              cursor: i === 0 ? "pointer" : "default",
            }}
          />
        ))}
        <Typography
          sx={{
            position: "absolute",
            left: "50%",
            transform: "translateX(-50%)",
            fontSize: 13,
            fontWeight: 600,
            color: "var(--dk-text, #1d1d1f)",
          }}
        >
          {t("ModelDetailInfo")}
        </Typography>
      </Box>

      <Box sx={{ px: 3, pt: 2.5, pb: 1, overflowY: "auto" }}>
        {/* 모델 정보 */}
        <Section
          title={t("ModelInfo")}
          action={
            !isEditingModel && (
              <Button
                onClick={() => setIsEditingModel(true)}
                sx={{ ...smallBtn("secondary"), py: 0.25 }}
              >
                {t("Edit")}
              </Button>
            )
          }
        >
          {isEditingModel ? (
            <>
              <Row label={t("InspModelName")}>
                <TextField
                  fullWidth
                  size="small"
                  autoFocus
                  value={modelForm.name}
                  onChange={(e) =>
                    setModelForm({ ...modelForm, name: e.target.value })
                  }
                  onKeyDown={(e) => e.key === "Enter" && handleSaveModel()}
                  sx={inputSx}
                />
              </Row>
              <Row label={t("Description")}>
                <TextField
                  fullWidth
                  size="small"
                  value={modelForm.description}
                  onChange={(e) =>
                    setModelForm({ ...modelForm, description: e.target.value })
                  }
                  inputProps={{ maxLength: 250 }}
                  sx={inputSx}
                />
              </Row>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: 1,
                  mt: 1,
                }}
              >
                <Button
                  onClick={() => {
                    setIsEditingModel(false);
                    setModelForm({
                      name: model.name,
                      description: model.description ?? "",
                    });
                  }}
                  sx={smallBtn("secondary")}
                >
                  {t("Cancel")}
                </Button>
                <Button onClick={handleSaveModel} sx={smallBtn("primary")}>
                  {t("Save")}
                </Button>
              </Box>
            </>
          ) : (
            <>
              <Row label={t("InspModelName")}>{model.name}</Row>
              <Row label={t("Description")}>{model.description || "-"}</Row>
              {projectId && (
                <Box
                  sx={{ display: "flex", justifyContent: "flex-end", pt: 1 }}
                >
                  <Typography
                    onClick={handleMoveProject}
                    sx={{
                      fontSize: 12.5,
                      color: "#007AFF",
                      cursor: "pointer",
                      "&:hover": { textDecoration: "underline" },
                    }}
                  >
                    {t("MoveToLinkedProjectMsg")}
                  </Typography>
                </Box>
              )}
            </>
          )}
        </Section>

        {/* 학습 통계 */}
        <Section title={t("TrainStatistics")}>
          <Box
            sx={{
              display: "flex",
              "& > *:not(:last-child)": {
                borderRight: "1px solid var(--dk-border, rgba(0,0,0,0.06))",
              },
            }}
          >
            <Stat
              label="과검"
              value={infoLoading ? "…" : fmt(performance?.overkill)}
              color="#ff9500"
            />
            <Stat
              label="미검"
              value={infoLoading ? "…" : fmt(performance?.missing)}
              color="#ff3b30"
            />
            <Stat
              label="임계값"
              value={infoLoading ? "…" : fmt(performance?.threshold)}
              color="#007AFF"
            />
          </Box>
        </Section>

        {/* 연결된 규칙 */}
        <Section title={`${t("LinkedRuleMsg")} (${rules.length})`}>
          {rules.map((rule) =>
            editingRuleId === rule.id ? (
              <Box
                key={rule.id}
                sx={{ py: 1, borderBottom: "1px solid var(--dk-border, rgba(0,0,0,0.06))" }}
              >
                <Typography sx={{ fontSize: 12, color: "var(--dk-text-2, #86868b)", mb: 1 }}>
                  {t("RuleEditMsg")}
                </Typography>
                {ruleFields(false)}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: 1,
                    mt: 1.5,
                  }}
                >
                  <Button
                    onClick={() => setEditingRuleId(null)}
                    sx={smallBtn("secondary")}
                  >
                    {t("Cancel")}
                  </Button>
                  <Button
                    onClick={() => handleSaveRule(rule.id)}
                    sx={smallBtn("primary")}
                  >
                    {t("Save")}
                  </Button>
                </Box>
              </Box>
            ) : (
              <Box
                key={rule.id}
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  py: 0.75,
                  borderBottom: "1px solid var(--dk-border, rgba(0,0,0,0.06))",
                }}
              >
                <Typography sx={{ fontSize: 13, color: "var(--dk-text, #1d1d1f)" }}>
                  {rule.name}
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => handleEditRule(rule)}
                  sx={{ color: "var(--dk-text-2, #86868b)" }}
                >
                  <Edit size={14} />
                </IconButton>
              </Box>
            ),
          )}

          {isAddingRule ? (
            <Box sx={{ pt: 1.5 }}>
              <Typography
                sx={{
                  fontSize: 12.5,
                  fontWeight: 600,
                  color: "var(--dk-text, #1d1d1f)",
                  mb: 1,
                }}
              >
                {t("AddNewRuleMsg")}
              </Typography>
              {ruleFields(true)}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: 1,
                  mt: 1.5,
                }}
              >
                <Button
                  onClick={() => setIsAddingRule(false)}
                  sx={smallBtn("secondary")}
                >
                  {t("Cancel")}
                </Button>
                <Button onClick={handleAddRule} sx={smallBtn("primary")}>
                  {t("AddRule")}
                </Button>
              </Box>
            </Box>
          ) : (
            <Button
              fullWidth
              onClick={() => {
                setIsAddingRule(true);
                setRuleForm(emptyRuleForm);
              }}
              sx={{
                ...smallBtn("secondary"),
                mt: rules.length ? 1.25 : 0,
                color: "#007AFF",
              }}
            >
              {`+ ${t("AddRule")}`}
            </Button>
          )}
        </Section>
      </Box>

      {/* 하단 액션 */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          px: 3,
          py: 1.5,
          borderTop: "1px solid var(--dk-border, rgba(0,0,0,0.06))",
          flexShrink: 0,
        }}
      >
        <Button onClick={handleDeleteModel} sx={{ ...macBtn("danger"), px: 2 }}>
          {t("Delete")}
        </Button>
        <Button onClick={onClose} sx={{ ...macBtn("secondary"), px: 2.5 }}>
          닫기
        </Button>
      </Box>
    </Dialog>
  );
}
